import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { VirtuousIntegration } from '@feathr/blackbox';
import type { IIntegrationUIOption, IIntegrationUIToggleSection } from '@feathr/components';
import { Alert, SyncRuleSection } from '@feathr/components';
import CrmOptionSelect from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/CrmOptionSelect';
import { getToggleField } from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPageV2/RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';

import {
  ACTIVITY_UPSYNC_KEY,
  CONTACT_DOWNSYNC_KEY,
  DONATION_UPSYNC_KEY,
  GIFT_DOWNSYNC_KEY,
} from '../virtuousConfig';
import { VIRTUOUS_ICON } from '../virtuousIcon';
import {
  getActivityValues,
  stageDownsyncActivity,
  stageUpsyncActivity,
} from '../VirtuousIntegrationPage.utils';
import VirtuousTabContent from '../VirtuousTabContent';
import {
  useVirtuousNoteTypeOptions,
  useVirtuousProjectOptions,
} from './VirtuousActivitiesTab.hooks';

interface IProps {
  integration: VirtuousIntegration;
  sections: readonly IIntegrationUIToggleSection[];
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

function VirtuousActivitiesTab({
  disabledReason,
  integration,
  sections,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const values = getActivityValues({
    downsync_activities: integration.get('downsync_activities'),
    upsync_activities: integration.get('upsync_activities'),
  });
  const projectOptions = useVirtuousProjectOptions(integration, !!values[DONATION_UPSYNC_KEY]);
  const noteTypeOptions = useVirtuousNoteTypeOptions(integration, !!values[ACTIVITY_UPSYNC_KEY]);

  // Stage the edit on the model; the page's Save & Sync persists it.
  function handleChange(key: string, value: boolean): void {
    switch (key) {
      case GIFT_DOWNSYNC_KEY:
        stageDownsyncActivity(integration, 'gifts', value);
        break;

      case DONATION_UPSYNC_KEY:
        stageUpsyncActivity(integration, 'donations', value);
        break;

      case ACTIVITY_UPSYNC_KEY:
        stageUpsyncActivity(integration, 'activity', value);
        break;
    }
  }

  function handleSelectProject(option: IIntegrationUIOption): void {
    integration.set({ fundraising: { ...integration.get('fundraising'), project_id: option.id } });
  }

  function handleSelectNoteType(option: IIntegrationUIOption): void {
    integration.set({ activity_note_type: option.id });
  }

  function renderSlot(key: string): ReactNode {
    const field = getToggleField(sections, key);
    if (key === GIFT_DOWNSYNC_KEY) {
      // A gift attaches to a Feathr person that contact sync keeps matched.
      return values[GIFT_DOWNSYNC_KEY] && !values[CONTACT_DOWNSYNC_KEY] ? (
        <Alert
          description={t('Turn on contact sync on the Mappings tab to sync gifts.')}
          title={t('Gifts sync only while contact sync is on.')}
          type={'warning'}
        />
      ) : null;
    }
    if (field?.type !== 'select' || !values[key]) {
      return null;
    }
    if (key === DONATION_UPSYNC_KEY) {
      return (
        <CrmOptionSelect
          disabledReason={disabledReason}
          field={field}
          name={'virtuous-project'}
          noOptionsMessage={t('No projects found')}
          onSelect={handleSelectProject}
          options={projectOptions}
          value={integration.get('fundraising')?.project_id}
        />
      );
    }
    return (
      <CrmOptionSelect
        disabledReason={disabledReason}
        field={field}
        name={'virtuous-note-type'}
        noOptionsMessage={t('No note types found')}
        onSelect={handleSelectNoteType}
        options={noteTypeOptions}
        value={integration.get('activity_note_type')}
      />
    );
  }

  return (
    <VirtuousTabContent
      inactiveLabel={t('Connect Virtuous to choose which activities sync.')}
      integration={integration}
    >
      {sections.map((section) => (
        <SyncRuleSection
          crmIcon={VIRTUOUS_ICON}
          disabledReason={disabledReason}
          key={section.key}
          name={`virtuous-${section.key}`}
          onChange={handleChange}
          renderSlot={renderSlot}
          section={section}
          values={values}
        />
      ))}
    </VirtuousTabContent>
  );
}

export default observer(VirtuousActivitiesTab);
